import type { MediaVariant } from "../../types";

export const publicMediaVariantLabel = (label: string): string =>
  label
    .replace(/手机版横屏/gi, "电脑版")
    .replace(/\s*[（(]Intel\s+Arc\s+A380\s+优先[）)]/gi, "")
    .replace(/Intel\s+Arc\s+A380/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

export const isLegacyMobileLandscapeVariant = (
  variant: MediaVariant,
): boolean =>
  variant.kind === "mobile" &&
  (/mobile-landscape/i.test(variant.id) || /手机版横屏/i.test(variant.label));

/**
 * Older imports classified ordinary landscape renditions as mobile. They are
 * device-agnostic files, so desktop playback must treat them as desktop
 * candidates for initial selection, ABR upgrades and every downshift tier.
 */
export const mergeLandscapeVariantIntoDesktop = (
  variant: MediaVariant,
): MediaVariant =>
  isLegacyMobileLandscapeVariant(variant)
    ? {
        ...variant,
        kind: "desktop",
        label: publicMediaVariantLabel(variant.label),
      }
    : variant;

export const mediaLocationLabel = (url: string): string => {
  if (
    /\.b-cdn\.net(?:\/|$)/i.test(url) ||
    /\/api\/videos\/[^/]+\/(?:mobile|desktop)-master\.m3u8(?:$|[?#])/i.test(url)
  ) {
    return "链路";
  }
  if (/\/frp-media\//i.test(url)) {
    return "等待 CDN 同步";
  }
  if (/127\.0\.0\.1|localhost|192\.168\./i.test(url)) {
    return "本地";
  }
  return "VPS 在线";
};

export const mediaVariantLabel = (variant: MediaVariant): string =>
  `${publicMediaVariantLabel(variant.label)} · ${mediaLocationLabel(variant.url)}`;

export const preferredVariant = (
  variants: MediaVariant[],
  fallbackUrl: string,
): MediaVariant | undefined =>
  variants.find((variant) => variant.url === fallbackUrl) ||
  variants.find((variant) => variant.isDefault) ||
  variants[0];

export const preferredPlaybackVariant = (
  variants: MediaVariant[],
  fallbackUrl: string,
  kind: "mobile" | "desktop",
): MediaVariant | undefined => {
  const matchingKind = variants.filter((variant) => variant.kind === kind);
  const fallbackMatch = matchingKind.find((variant) => variant.url === fallbackUrl);
  if (fallbackMatch) return fallbackMatch;

  const automaticHls = matchingKind.find((variant) =>
    /master\.m3u8(?:$|[?#])/i.test(variant.url),
  );
  if (automaticHls) return automaticHls;

  const hlsDefault = matchingKind.find(
    (variant) =>
      variant.isDefault &&
      (/\.m3u8(?:$|[?#])/i.test(variant.url) || /^hls/i.test(variant.format)),
  );
  if (hlsDefault) return hlsDefault;

  const desiredQuality = kind === "desktop" ? "720p" : "480p";
  return (
    matchingKind.find((variant) =>
      `${variant.quality} ${variant.resolution} ${variant.label}`
        .toLowerCase()
        .includes(desiredQuality),
    ) ||
    matchingKind.find((variant) => variant.isDefault) ||
    matchingKind[0] ||
    preferredVariant(variants, fallbackUrl)
  );
};
